import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  LinearProgress,
  IconButton,
  Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuthContext } from '../../context/AuthContext';

const QuizForceOverlay = () => {
  const { isAuthenticated } = useAuthContext();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState(null);
  const [timeLimit, setTimeLimit] = useState(30);
  const [timeLeft, setTimeLeft] = useState(30);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [isAnswered, setIsAnswered] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [answerResult, setAnswerResult] = useState(null);
  const timedOutRef = React.useRef(false);

  const handleEvent = useCallback(
    (e) => {
      if (!isAuthenticated) return;
      const payload = e?.detail || {};
      const q = payload.question;
      if (!q || !q.question_text) return;
      setQuestion(q);
      const limit = payload.time_limit_seconds || q.time_limit_seconds || q.base_time || 30;
      setTimeLimit(limit);
      setTimeLeft(limit);
      setSelectedAnswer('');
      setIsAnswered(false);
      setShowResult(false);
      setAnswerResult(null);
      timedOutRef.current = false;
      setOpen(true);
    },
    [isAuthenticated]
  );

  useEffect(() => {
    window.addEventListener('quiz-force-question', handleEvent);
    return () => window.removeEventListener('quiz-force-question', handleEvent);
  }, [handleEvent]);

  useEffect(() => {
    if (!open || isAnswered || timeLeft <= 0) return;
    const t = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [open, isAnswered, timeLeft]);

  const handleTimeout = useCallback(() => {
    if (timedOutRef.current || !question) return;
    timedOutRef.current = true;
    setIsAnswered(true);
    const correctAns = question.answers?.find((a) => a.is_correct === 1 || a.is_correct === true);
    setAnswerResult({
      correct: false,
      message: "Time's up!",
      correctAnswer: correctAns?.answer_text
    });
    setShowResult(true);
  }, [question]);

  const handleAnswerSelect = (answerId) => {
    if (isAnswered) return;
    setSelectedAnswer(String(answerId));
  };

  const handleSubmit = () => {
    if (!selectedAnswer || !question || isAnswered) return;
    setIsAnswered(true);
    const chosen = question.answers?.find((a) => String(a.answer_id) === String(selectedAnswer));
    const correctAns = question.answers?.find((a) => a.is_correct === 1 || a.is_correct === true);
    const correct = chosen?.is_correct === 1 || chosen?.is_correct === true;
    setAnswerResult({
      correct,
      message: correct ? 'Correct!' : 'Incorrect!',
      correctAnswer: correct ? null : correctAns?.answer_text
    });
    setShowResult(true);
  };

  const handleClose = () => {
    setOpen(false);
    setQuestion(null);
    setShowResult(false);
    setAnswerResult(null);
  };

  const getTimeColor = () => {
    if (timeLeft <= 5) return '#f44336';
    if (timeLeft <= 10) return '#ff9800';
    return '#4caf50';
  };

  const getProgressValue = () => {
    return ((timeLimit - timeLeft) / timeLimit) * 100;
  };

  if (!open || !question) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={!isAnswered}
      disableBackdropClick={!isAnswered}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '16px'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <IconButton
          onClick={handleClose}
          disabled={!isAnswered}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: '#fff',
            opacity: isAnswered ? 1 : 0.5,
            zIndex: 10
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ p: 3, pt: 4 }}>
          {/* Timer */}
          {!isAnswered && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  color: getTimeColor(),
                  fontWeight: 700,
                  minWidth: 48
                }}
              >
                {timeLeft}s
              </Typography>
              <LinearProgress
                variant="determinate"
                value={getProgressValue()}
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getTimeColor(),
                    borderRadius: 4
                  }
                }}
              />
            </Box>
          )}

          <Typography
            variant="h5"
            sx={{
              color: '#fff',
              fontWeight: 600,
              mb: 3,
              pr: 4
            }}
          >
            {question.question_text}
          </Typography>

          {question.question_clue && (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, fontStyle: 'italic' }}>
              💡 {question.question_clue}
            </Typography>
          )}

          <RadioGroup
            value={selectedAnswer}
            onChange={(e) => handleAnswerSelect(e.target.value)}
          >
            <Grid container spacing={2}>
              {question.answers?.map((answer, idx) => (
                <Grid item xs={12} sm={6} key={answer.answer_id}>
                  <Box
                    onClick={() => !isAnswered && handleAnswerSelect(answer.answer_id)}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: selectedAnswer === String(answer.answer_id) ? '#888' : '#444',
                      borderRadius: '8px',
                      cursor: isAnswered ? 'default' : 'pointer',
                      bgcolor: selectedAnswer === String(answer.answer_id) ? 'rgba(255,255,255,0.08)' : 'transparent'
                    }}
                  >
                    <FormControlLabel
                      value={String(answer.answer_id)}
                      control={<Radio sx={{ color: '#888', "&.Mui-checked": { color: '#fff' } }} />}
                      disabled={isAnswered}
                      label={
                        <Typography sx={{ color: '#fff' }}>
                          {String.fromCharCode(65 + idx)}. {answer.answer_text}
                        </Typography>
                      }
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </RadioGroup>

          {!isAnswered && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!selectedAnswer}
                sx={{
                  bgcolor: '#9C27B0',
                  '&:hover': { bgcolor: '#7B1FA2' }
                }}
              >
                Submit Answer
              </Button>
            </Box>
          )}

          {showResult && answerResult && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: '8px',
                bgcolor: answerResult.correct ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                border: '1px solid',
                borderColor: answerResult.correct ? '#4caf50' : '#f44336',
                textAlign: 'center'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                {answerResult.correct ? (
                  <CheckIcon sx={{ color: '#4caf50', fontSize: 32 }} />
                ) : (
                  <CancelIcon sx={{ color: '#f44336', fontSize: 32 }} />
                )}
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                  {answerResult.message}
                </Typography>
              </Box>
              {!answerResult.correct && answerResult.correctAnswer && (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Correct answer: {answerResult.correctAnswer}
                </Typography>
              )}
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{ mt: 2, color: '#fff', borderColor: '#666' }}
              >
                Close
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default QuizForceOverlay;
